import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { usePaymentStatus } from "../../application/hooks/usePaymentStatus";
import { cart } from "../../application/store/cartStore";

/**
 * Pantalla de espera del cobro con bitcoin.
 *
 * A diferencia de v1, acá sólo viaja el `invoice` id por la URL: el monto, el bolt11 y el
 * vencimiento se leen del backend. Antes iban seis parámetros en el querystring, así que
 * cualquiera podía editar la URL y ver un QR con un monto que no correspondía al cobro.
 */
export default function PaymentPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const invoiceId = params.get("invoice");

  const { invoice, confirmed, expired } = usePaymentStatus(invoiceId);
  const [ahora, setAhora] = useState(() => Date.now());

  // Reloj para el contador. Se apoya en `expires_at` del backend, no en un contador local:
  // si la pestaña queda dormida, al volver muestra el tiempo real, no uno congelado.
  useEffect(() => {
    const timer = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!confirmed || !invoice) return;
    cart.clear();
    navigate(
      `/pos/confirmed?mxn=${invoice.amount_mxn}&sats=${invoice.amount_sats}&metodo=lightning`,
      { replace: true }
    );
  }, [confirmed, invoice, navigate]);

  useEffect(() => {
    if (!invoiceId) navigate("/pos", { replace: true });
  }, [invoiceId, navigate]);

  if (!invoiceId) return null;

  if (!invoice) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-text-secondary">Generando cobro...</p>
      </div>
    );
  }

  const segundosRestantes = Math.max(
    0,
    Math.floor((Date.parse(invoice.expires_at) - ahora) / 1000)
  );
  const minutes = Math.floor(segundosRestantes / 60);
  const seconds = segundosRestantes % 60;
  const vencido = expired || segundosRestantes <= 0;

  // Volver al POS NO cancela la orden: en v2 una orden `invoiced` con QR vivo todavía puede
  // pagarla el cliente, y cerrarla desde acá arriesga dar por perdida una venta que sí entró.
  // Si nadie paga, expira sola y no cuenta en los reportes.
  const volver = () => {
    navigate("/pos", { replace: true });
  };

  return (
    <div className="flex min-h-full flex-col items-center justify-center px-4">
      <button
        onClick={volver}
        className="mb-6 self-start text-sm text-text-secondary hover:text-text-primary"
      >
        &larr; Volver
      </button>

      <p className="mb-1 font-mono text-2xl font-bold">
        ${Number(invoice.amount_mxn).toFixed(2)} MXN
      </p>
      <p className="mb-6 font-mono text-lg text-accent">
        {invoice.amount_sats.toLocaleString()} sats
      </p>

      <div className="rounded-xl bg-white p-4">
        <QRCodeSVG
          value={invoice.bolt11.toUpperCase()}
          size={256}
          level="M"
          style={{ width: "100%", maxWidth: 256, height: "auto" }}
        />
      </div>

      {vencido ? (
        <div className="mt-6 space-y-3 text-center">
          <p className="text-error" data-testid="invoice-expirado">
            El cobro expiró
          </p>
          <button
            onClick={volver}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-bold text-bg-primary hover:bg-accent-hover"
          >
            Generar nuevo
          </button>
        </div>
      ) : (
        <div className="mt-6 text-center">
          <div className="flex items-center gap-2 text-warning">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-warning" />
            <span className="text-sm">Esperando pago...</span>
          </div>
          <p className="mt-1 font-mono text-xs text-text-secondary">
            Expira en {minutes}:{seconds.toString().padStart(2, "0")}
          </p>
        </div>
      )}
    </div>
  );
}
